/* ==========================================================================
   T.P. Mills Putter Finder - scoring engine (Phase C-3B)

   Rule-based weighted scoring. No AI, no LLM, no black box.
   Every point a product earns comes from a Shopify metafield value exposed by
   snippets/putter-finder-data.liquid, compared against a captured answer.

   Exposed as window.PutterFinderScoring so it can be loaded and reviewed on
   its own, and unit-tested outside the browser.
   ========================================================================== */
(function (global) {
  'use strict';

  /* Axis weights. Sum of the seven axes = 110 before the BEST bonus.
     An axis whose answer is "Not Sure" is removed from BOTH the earned score
     and the maximum, so unanswered axes never penalise a product. */
  var WEIGHTS = {
    stroke_shape: 40,
    face_control: 15,
    visual_comfort: 15,
    putter_purpose: 12,
    line_confidence: 10,
    touch_feedback: 10,
    length_preference: 8,
  };

  var BEST_BONUS = 3;
  var NEUTRAL = ['not_sure', 'not_sure_yet'];

  /* Products with an approved blank custom.balance. They are reachable through
     the tier path but earn no stroke-balance points. Documented exception. */
  var BALANCE_EXEMPT = [
    't-p-mills-sgss-crocodile-handmade-putter',
    'sgss-flat-t-lucky-charm-light-gold-handmade-putter',
  ];

  function pick(map, key, fallback) {
    if (key == null) return fallback;
    return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : fallback;
  }

  function hasLength(product, inches) {
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!v.available) continue;
      if (String(v.title).indexOf(inches) !== -1) return true;
    }
    return false;
  }

  /* A single-variant made-to-order piece (handmade) has no length option:
     length is selected before shipment. Treated as a partial match. */
  function isMadeToOrderLength(product) {
    var variants = product.variants || [];
    return variants.length === 1 && /default/i.test(String(variants[0].title));
  }

  function anyAvailable(product) {
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) if (variants[i].available) return true;
    return false;
  }

  /* ---------------------------------------------------------------- axes ---
     Each axis function returns a fit fraction between 0 and 1, or null when
     the axis is inactive for this answer. */

  function strokeShapeFit(answer, p) {
    if (BALANCE_EXEMPT.indexOf(p.handle) !== -1 || !p.balance) return 0;
    var table = {
      mostly_straight: { zero_torque: 1, face_balanced: 0.9, slight_toe: 0.3, strong_toe: 0.05 },
      slight_arc: { slight_toe: 1, strong_toe: 0.6, face_balanced: 0.5, zero_torque: 0.45 },
      stronger_arc: { strong_toe: 1, slight_toe: 0.7, face_balanced: 0.2, zero_torque: 0.15 },
    };
    return pick(pick(table, answer, {}), p.balance, 0);
  }

  function faceControlFit(answer, p) {
    var alignmentHelp = p.alignment === 'three_line' ? 1 : p.alignment === 'dual' ? 0.7 : p.alignment === 'single' ? 0.35 : 0;
    var stability = pick({ mallet: 1, wide_blade: 0.8, compact_mallet: 0.65, blade: 0.2 }, p.head_shape, 0);

    if (answer === 'feels_open') {
      var openBalance = pick({ zero_torque: 1, face_balanced: 0.9, slight_toe: 0.35, strong_toe: 0.2 }, p.balance, 0);
      return openBalance * 0.6 + stability * 0.25 + alignmentHelp * 0.15;
    }
    if (answer === 'turns_over') {
      /* toe hang helps a pull tendency; zero torque is deliberately capped */
      var overBalance = pick({ strong_toe: 1, slight_toe: 0.9, face_balanced: 0.4, zero_torque: 0.35 }, p.balance, 0);
      return overBalance * 0.8 + alignmentHelp * 0.2;
    }
    if (answer === 'speed_changes') {
      return p.head_weight_g ? 1 : 0.2;
    }
    if (answer === 'contact_varies') {
      return stability * 0.7 + alignmentHelp * 0.3;
    }
    return null;
  }

  function visualComfortFit(answer, p) {
    if (answer === 'zero_torque') {
      if (p.balance === 'zero_torque') return 1;
      return pick({ mallet: 0.3, compact_mallet: 0.2 }, p.head_shape, 0.1);
    }
    var table = {
      classic_blade: { blade: 1, wide_blade: 0.5, compact_mallet: 0.15, mallet: 0.05 },
      wide_blade: { wide_blade: 1, blade: 0.55, compact_mallet: 0.4, mallet: 0.15 },
      compact_mallet: { compact_mallet: 1, mallet: 0.6, wide_blade: 0.4, blade: 0.1 },
    };
    var row = pick(table, answer, null);
    if (!row) return null;
    return pick(row, p.head_shape, 0);
  }

  function touchFeedbackFit(answer, p) {
    if (answer === 'soft_muted') {
      /* material is a weak proxy for feel; scored conservatively on purpose */
      return pick({ sgss: 0.8, forged_303: 0.6, forged_303_6061: 0.5, aluminum_303: 0.4 }, p.material, 0.3);
    }
    if (answer === 'direct_feedback') {
      var shape = pick({ blade: 1, wide_blade: 0.7, compact_mallet: 0.3, mallet: 0.15 }, p.head_shape, 0);
      var bal = pick({ slight_toe: 1, strong_toe: 0.9, face_balanced: 0.35, zero_torque: 0.2 }, p.balance, 0);
      var tier = p.tier === 'tour_issue' ? 1 : p.tier === 'handmade' || p.tier === 'sgss' ? 0.6 : 0.5;
      return shape * 0.45 + bal * 0.35 + tier * 0.2;
    }
    if (answer === 'stable_quiet') {
      var quietBal = pick({ zero_torque: 1, face_balanced: 0.8, slight_toe: 0.25, strong_toe: 0.15 }, p.balance, 0);
      var quietShape = pick({ mallet: 1, compact_mallet: 0.75, wide_blade: 0.55, blade: 0.2 }, p.head_shape, 0);
      return quietBal * 0.65 + quietShape * 0.35;
    }
    if (answer === 'speed_control') {
      return p.head_weight_g ? 1 : 0.2;
    }
    return null;
  }

  function putterPurposeFit(answer, p) {
    if (answer === 'stability_upgrade') {
      return pick({ zero_torque: 1, face_balanced: 0.8, slight_toe: 0.25, strong_toe: 0.15 }, p.balance, 0.2);
    }
    var table = {
      everyday_gamer: { club_champ: 1, tour_issue: 0.6, sgss: 0.2, handmade: 0.1 },
      tour_inspired: { tour_issue: 1, club_champ: 0.5, sgss: 0.4, handmade: 0.4 },
      handmade_collectible: { handmade: 1, sgss: 0.95, tour_issue: 0.15, club_champ: 0.1 },
    };
    var row = pick(table, answer, null);
    if (!row) return null;
    return pick(row, p.tier, 0);
  }

  function lineConfidenceFit(answer, p) {
    var alignmentHelp = p.alignment === 'three_line' ? 1 : p.alignment === 'dual' ? 0.7 : p.alignment === 'single' ? 0.35 : 0;
    if (answer === 'short_putts') {
      var bal = pick({ zero_torque: 1, face_balanced: 0.85, slight_toe: 0.3, strong_toe: 0.2 }, p.balance, 0);
      var shape = pick({ mallet: 1, compact_mallet: 0.75, wide_blade: 0.6, blade: 0.25 }, p.head_shape, 0);
      return bal * 0.55 + shape * 0.25 + alignmentHelp * 0.2;
    }
    if (answer === 'distance_control') return p.head_weight_g ? 1 : 0.2;
    if (answer === 'start_line') {
      var ztBal = pick({ zero_torque: 1, face_balanced: 0.7, slight_toe: 0.3, strong_toe: 0.2 }, p.balance, 0);
      return ztBal * 0.6 + alignmentHelp * 0.4;
    }
    if (answer === 'strike_consistency') {
      return pick({ mallet: 1, wide_blade: 0.85, compact_mallet: 0.7, blade: 0.2 }, p.head_shape, 0);
    }
    if (answer === 'craft_rarity') {
      return pick({ handmade: 1, sgss: 1, tour_issue: 0.2, club_champ: 0.1 }, p.tier, 0);
    }
    return null;
  }

  function lengthFit(answer, p) {
    var inches = { len_33: '33', len_34: '34', len_35: '35' };
    var want = pick(inches, answer, null);
    if (!want) return null;
    if (hasLength(p, want)) return 1;
    if (isMadeToOrderLength(p) && anyAvailable(p)) return 0.8;
    return 0;
  }

  var AXES = {
    stroke_shape: strokeShapeFit,
    face_control: faceControlFit,
    visual_comfort: visualComfortFit,
    touch_feedback: touchFeedbackFit,
    putter_purpose: putterPurposeFit,
    line_confidence: lineConfidenceFit,
    length_preference: lengthFit,
  };

  var AXIS_LABEL = {
    stroke_shape: 'Stroke Shape',
    face_control: 'Face Control',
    visual_comfort: 'Visual Comfort',
    touch_feedback: 'Touch Feedback',
    putter_purpose: 'Putter Purpose',
    line_confidence: 'Line Confidence',
    length_preference: 'Length Preference',
  };

  /* -------------------------------------------------------------- scoring */

  function scoreProduct(product, answers) {
    var earned = 0;
    var max = 0;
    var parts = [];

    Object.keys(WEIGHTS).forEach(function (axis) {
      var answer = answers[axis];
      if (!answer) return;
      if (NEUTRAL.indexOf(answer.value) !== -1) return; // axis switched off
      var fit = AXES[axis](answer.value, product);
      if (fit === null) return;
      var weight = WEIGHTS[axis];
      max += weight;
      var points = fit * weight;
      earned += points;
      parts.push({ axis: axis, label: AXIS_LABEL[axis], answer: answer, weight: weight, fit: fit, points: points });
    });

    var bonus = (product.display_tags || []).indexOf('BEST') !== -1 ? BEST_BONUS : 0;
    var pct = max > 0 ? Math.min(100, Math.round(((earned + bonus) / max) * 100)) : null;

    /* Data completeness is used only to break ties between equal scores.
       It never adds points. */
    var completeness = ['balance', 'head_shape', 'head_weight_g', 'neck', 'alignment', 'material', 'finish', 'tier']
      .filter(function (f) { return product[f] !== null && product[f] !== undefined && product[f] !== ''; }).length;

    parts.sort(function (a, b) { return b.points - a.points; });

    return {
      product: product,
      earned: earned,
      bonus: bonus,
      max: max,
      pct: pct,
      neutral: max === 0,
      completeness: completeness,
      parts: parts,
      available: anyAvailable(product),
      inquiryOnly: product.tier === 'handmade' || product.tier === 'sgss',
    };
  }

  /* Reason bullets are generated from the metafield value that actually
     earned the points. Nothing is hardcoded per product. */
  var VALUE_TEXT = {
    zero_torque: 'a Zero Torque balance',
    face_balanced: 'a face-balanced head',
    slight_toe: 'a slight toe hang',
    strong_toe: 'a strong toe hang',
    blade: 'a blade profile',
    wide_blade: 'a wide-blade profile',
    compact_mallet: 'a compact mallet profile',
    mallet: 'a mallet profile',
    three_line: 'three-line alignment',
    dual: 'dual sightlines',
    single: 'a single sightline',
    none: 'no sightline',
    club_champ: 'the Club Champ line',
    tour_issue: 'the Tour Issue line',
    sgss: 'an SGSS build',
    handmade: 'a handmade build',
  };

  function describe(value) {
    return Object.prototype.hasOwnProperty.call(VALUE_TEXT, value) ? VALUE_TEXT[value] : value;
  }

  function reasonsFor(result, limit) {
    var p = result.product;
    var out = [];
    for (var i = 0; i < result.parts.length && out.length < (limit || 3); i++) {
      var part = result.parts[i];
      if (part.fit <= 0) continue;
      var v = part.answer.value;
      var lower = part.answer.label.toLowerCase();
      var text = null;

      switch (part.axis) {
        case 'stroke_shape':
          text = describe(p.balance) + ' suits your ' + lower + ' stroke.';
          break;

        case 'visual_comfort':
          text = describe(p.head_shape) + ' matches the ' + lower + ' look you chose.';
          break;

        /* Face Control scores on different fields per answer, so the reason
           must name the field that actually earned the points. */
        case 'face_control':
          if (v === 'speed_changes') {
            text = p.head_weight_g
              ? 'A published ' + p.head_weight_g + ' g head weight gives a reference for speed.'
              : 'Head weight is not published for this model.';
          } else if (v === 'contact_varies') {
            text = describe(p.head_shape) + (p.alignment ? ' with ' + describe(p.alignment) : '') +
                   ' adds stability when contact varies.';
          } else {
            text = describe(p.balance) + ' addresses a face that ' +
                   (v === 'feels_open' ? 'feels open' : 'turns over') + '.';
          }
          break;

        case 'putter_purpose':
          text = v === 'stability_upgrade'
            ? describe(p.balance) + ' is what makes this a stability upgrade.'
            : describe(p.tier) + ' fits the role you picked: ' + lower + '.';
          break;

        case 'line_confidence':
          if (v === 'distance_control') {
            text = p.head_weight_g
              ? 'A published ' + p.head_weight_g + ' g head weight supports distance control.'
              : 'Head weight is not published for this model.';
          } else if (v === 'craft_rarity') {
            text = describe(p.tier) + ' answers your interest in craft and rarity.';
          } else if (v === 'strike_consistency') {
            text = describe(p.head_shape) + ' holds up better when strike quality varies.';
          } else if (v === 'start_line') {
            text = describe(p.balance) + (p.alignment ? ' and ' + describe(p.alignment) : '') +
                   ' support a repeatable start line.';
          } else {
            text = describe(p.balance) + (p.alignment ? ' with ' + describe(p.alignment) : '') +
                   ' helps short-putt confidence.';
          }
          break;

        case 'touch_feedback':
          if (v === 'speed_control') {
            text = p.head_weight_g
              ? 'A published ' + p.head_weight_g + ' g head weight supports speed control.'
              : 'Head weight is not published for this model.';
          } else if (v === 'soft_muted') {
            text = describe(p.material) + ' is the material behind the feel.';
          } else if (v === 'direct_feedback') {
            text = describe(p.head_shape) + ' with ' + describe(p.balance) + ' gives more direct feedback.';
          } else {
            text = describe(p.balance) + ' with ' + describe(p.head_shape) +
                   ' keeps the head quieter through impact.';
          }
          break;

        case 'length_preference':
          text = part.fit === 1
            ? part.answer.label + ' is currently available.'
            : 'Final length is selected before shipment.';
          break;
      }
      if (text) out.push(text);
    }
    if (!out.length && p.fit_notes) out.push(String(p.fit_notes).split('. ')[0] + '.');
    return out;
  }

  /* ------------------------------------------------ low-information paths ---
     Step 1 (Line Confidence) is a FORCED choice: it has no "Not Sure" option
     and none will be added. A customer can therefore always reach the result
     screen having given exactly one meaningful answer. One answer is not a fit
     profile, and printing a percentage in that case would be inventing
     precision we do not have.

     ANCHOR_AXES are the three axes that carry enough signal to justify a
     percentage. LOW_INFO_MIN_WEIGHT is the effective active weight below which
     we refuse to score at all. */

  var ANCHOR_AXES = ['stroke_shape', 'visual_comfort', 'putter_purpose'];
  var LOW_INFO_MIN_WEIGHT = 35;

  function isMeaningful(answers, axis) {
    var a = answers && answers[axis];
    if (!a) return false;
    return NEUTRAL.indexOf(a.value) === -1;
  }

  function meaningfulAxes(answers) {
    return Object.keys(WEIGHTS).filter(function (axis) { return isMeaningful(answers, axis); });
  }

  /* Answer-derived, never product-derived. Every axis function decides null vs
     number from the ANSWER alone, so the active weight is identical for every
     product in the dataset. Computing it here keeps the low-information
     decision independent of which products happen to be loaded. */
  function activeWeight(answers) {
    return meaningfulAxes(answers).reduce(function (sum, axis) { return sum + WEIGHTS[axis]; }, 0);
  }

  function lowInformation(answers) {
    var meaningful = meaningfulAxes(answers);
    var weight = activeWeight(answers);
    var anchors = ANCHOR_AXES.filter(function (axis) { return isMeaningful(answers, axis); });

    var reasons = [];
    if (meaningful.length <= 1) reasons.push('only_step_1_answered');
    if (weight < LOW_INFO_MIN_WEIGHT) reasons.push('active_weight_below_' + LOW_INFO_MIN_WEIGHT);
    if (anchors.length === 0) reasons.push('no_anchor_axis_answered');

    return {
      low: reasons.length > 0,
      reasons: reasons,
      activeWeight: weight,
      meaningfulCount: meaningful.length,
      meaningfulAxes: meaningful,
      anchorAxes: anchors,
    };
  }

  /* Merchandising directions for low-information paths. These are OWNER-CHOSEN
     starting points, not computed fits. They live in their own table, they are
     never mixed into scoring, and they are reported separately from best-match
     concentration so a fallback choice can never be mistaken for a scoring
     outcome. */
  var DIRECTIONS = {
    short_putts: {
      key: 'stability_zt',
      label: 'Stability / ZT Direction',
      handles: [
        'club-champ-vista-zt-zero-torque-putter-right-handed',
        'club-champ-solana-zt-zero-torque-putter',
      ],
    },
    distance_control: {
      key: 'touch_feedback',
      label: 'Touch / Feedback Direction',
      handles: ['new-tour-issue-23', 'club-champ-flat-t-pvd'],
    },
    start_line: {
      key: 'line_confidence',
      label: 'Line Confidence Direction',
      handles: [
        'club-champ-vista-zt-zero-torque-putter-right-handed',
        'club-champ-nelly-zt-zero-torque-putter',
      ],
    },
    strike_consistency: {
      key: 'stability',
      label: 'Stability Direction',
      handles: ['club-champ-vista-pvd', 'club-champ-nelly-pvd-1'],
    },
    craft_rarity: {
      key: 'craft',
      label: 'Craft Direction',
      /* The owner specified the SGSS / Handmade collection rather than two
         fixed SKUs, because these are one-of-one pieces whose availability
         turns over. Resolved from tier so the direction survives inventory
         changes without a code edit. */
      tiers: ['handmade', 'sgss'],
      collectionLabel: 'SGSS / Handmade Collection',
      limit: 3,
    },
  };

  var DIRECTION_COPY = [
    'Your answers point to an early direction, but not enough to make a full Mills Match Profile yet.',
    'Start by comparing these T.P. Mills directions.',
  ];

  function directionFor(answers, products) {
    var a = answers && answers.line_confidence;
    var spec = a ? pick(DIRECTIONS, a.value, null) : null;
    if (!spec) return null;

    var list = products || [];
    var byHandle = {};
    list.forEach(function (p) { byHandle[p.handle] = p; });

    var chosen = [];
    var missing = [];

    if (spec.handles) {
      spec.handles.forEach(function (h) {
        if (byHandle[h]) chosen.push(byHandle[h]);
        else missing.push(h);
      });
    }
    if (spec.tiers) {
      list
        .filter(function (p) { return spec.tiers.indexOf(p.tier) !== -1; })
        .sort(function (x, y) { return String(x.handle).localeCompare(String(y.handle)); })
        .forEach(function (p) {
          if (chosen.length < (spec.limit || 2) && chosen.indexOf(p) === -1) chosen.push(p);
        });
    }

    return {
      key: spec.key,
      label: spec.label,
      collectionLabel: spec.collectionLabel || null,
      copy: DIRECTION_COPY,
      answer: a,
      products: chosen.map(function (p) {
        return {
          product: p,
          available: anyAvailable(p),
          inquiryOnly: p.tier === 'handmade' || p.tier === 'sgss',
        };
      }),
      /* configured handles absent from the current dataset, surfaced so a
         silent drop is impossible */
      missingHandles: missing,
    };
  }

  /* ------------------------------------------------ customer-facing labels ---
     Phase C-3B. The raw percentage stays internal: it drives ranking and the
     dataLayer, and it is never printed for a customer.

     Label strength is decided by SCORE ALONE. Spread to the runner-up is a
     separate, additive signal: when the top two are within SPREAD_TIGHT the
     result carries a sub-line inviting comparison, but the headline label is
     NOT downgraded. A 100% fit with an equally good runner-up is still a
     Strong Match, because it is one; telling that customer "Close Match" read
     as "not quite right" and suppressed confidence exactly where the engine
     was most certain. (C-3 shipped the downgrade; this reverses it.) */

  var SPREAD_TIGHT = 3;
  var STRONG_MIN = 85;
  var GOOD_MIN = 70;

  var MATCH_LABELS = {
    starting_direction: 'Starting Direction',
    strong_match: 'Strong Match',
    good_match: 'Good Match',
    exploratory_match: 'Exploratory Match',
  };

  var CLOSE_ALTERNATIVES_NOTE = 'Two alternatives score very closely \u2014 worth comparing.';

  function matchLabel(ranked) {
    if (!ranked || ranked.lowInformation || !ranked.best) {
      return {
        key: 'starting_direction',
        label: MATCH_LABELS.starting_direction,
        spread: null,
        pct: null,
        closeAlternatives: false,
        note: null,
      };
    }
    var pct = ranked.best.pct;
    var alt = ranked.alternatives && ranked.alternatives[0];
    var spread = alt && alt.pct !== null && pct !== null ? pct - alt.pct : null;

    var key;
    if (pct !== null && pct >= STRONG_MIN) key = 'strong_match';
    else if (pct !== null && pct >= GOOD_MIN) key = 'good_match';
    else key = 'exploratory_match';

    var close = spread !== null && spread <= SPREAD_TIGHT;

    return {
      key: key,
      label: MATCH_LABELS[key],
      spread: spread,
      pct: pct,
      closeAlternatives: close,
      note: close ? CLOSE_ALTERNATIVES_NOTE : null,
    };
  }

  /* ------------------------------------------------------------- profile ---
     A deterministic name and one-line summary assembled from the answers the
     customer actually gave. Nothing is invented and nothing is random: the
     same answers always produce the same profile name. */

  var STROKE_WORD = {
    mostly_straight: 'Straight-Line',
    slight_arc: 'Slight-Arc',
    stronger_arc: 'Arc',
  };
  var PURPOSE_WORD = {
    everyday_gamer: 'Everyday',
    stability_upgrade: 'Stability',
    tour_inspired: 'Tour Feel',
    handmade_collectible: 'Collector',
  };
  var SHAPE_WORD = {
    classic_blade: 'Blade',
    wide_blade: 'Wide Blade',
    compact_mallet: 'Compact Mallet',
    zero_torque: 'Zero Torque',
  };
  var CONCERN_WORD = {
    short_putts: 'Short Range',
    distance_control: 'Distance',
    start_line: 'Start Line',
    strike_consistency: 'Strike',
    craft_rarity: 'Craft',
  };
  var CONCERN_PHRASE = {
    short_putts: 'short putts',
    distance_control: 'distance control',
    start_line: 'starting the ball on line',
    strike_consistency: 'strike consistency',
    craft_rarity: 'craft and rarity',
  };
  var STROKE_PHRASE = {
    mostly_straight: 'a mostly straight stroke',
    slight_arc: 'a slight arc',
    stronger_arc: 'a stronger arc',
  };

  function answerValue(answers, axis) {
    var a = answers && answers[axis];
    if (!a || NEUTRAL.indexOf(a.value) !== -1) return null;
    return a.value;
  }

  function profileFor(answers) {
    var stroke = answerValue(answers, 'stroke_shape');
    var purpose = answerValue(answers, 'putter_purpose');
    var shape = answerValue(answers, 'visual_comfort');
    var concern = answerValue(answers, 'line_confidence');

    var first = pick(STROKE_WORD, stroke, null);
    var second = pick(PURPOSE_WORD, purpose, null) || pick(SHAPE_WORD, shape, null) || pick(CONCERN_WORD, concern, null);

    var words = [first, second].filter(Boolean);
    var name = words.length ? words.join(' ') + ' Profile' : 'Starting Profile';

    var bits = [];
    if (stroke) bits.push('You described ' + STROKE_PHRASE[stroke] + '.');
    if (concern) bits.push('What costs you the most confidence is ' + CONCERN_PHRASE[concern] + '.');
    if (shape && SHAPE_WORD[shape]) bits.push('At address you want ' + SHAPE_WORD[shape].toLowerCase() + '.');
    else if (purpose && PURPOSE_WORD[purpose]) bits.push('The role you picked is ' + PURPOSE_WORD[purpose].toLowerCase() + '.');

    return { name: name, summary: bits.join(' ') };
  }

  /* Available lengths, read from variant titles of AVAILABLE variants only.
     Handmade single-variant pieces have no length option: length is set before
     shipment, so they report made-to-order rather than a false empty list. */
  function lengthsFor(product) {
    var out = [];
    (product.variants || []).forEach(function (v) {
      if (!v.available) return;
      var m = String(v.title).match(/3[2-6]/);
      if (m && out.indexOf(m[0]) === -1) out.push(m[0]);
    });
    out.sort();
    if (!out.length && isMadeToOrderLength(product) && anyAvailable(product)) return { madeToOrder: true, list: [] };
    return { madeToOrder: false, list: out };
  }

  function rank(products, answers) {
    var info = lowInformation(answers);
    var scored = (products || []).map(function (p) { return scoreProduct(p, answers); });
    scored.sort(function (a, b) {
      var ap = a.pct === null ? -1 : a.pct;
      var bp = b.pct === null ? -1 : b.pct;
      if (bp !== ap) return bp - ap;
      if (b.earned !== a.earned) return b.earned - a.earned;
      if (a.available !== b.available) return a.available ? -1 : 1;   // in stock first
      if (b.bonus !== a.bonus) return b.bonus - a.bonus;              // BEST tag
      if (b.completeness !== a.completeness) return b.completeness - a.completeness;
      return String(a.product.handle).localeCompare(String(b.product.handle));
    });
    /* Alternatives are diversified: when several products tie, prefer ones
       that differ from the best match in head shape or tier, so the two
       alternatives are actually alternatives and not near-duplicates.
       Deterministic, and it never reorders products of a higher score. */
    var best = scored[0] || null;
    var alternatives = [];
    if (best) {
      var rest = scored.slice(1);
      var differs = rest.filter(function (r) {
        return r.product.head_shape !== best.product.head_shape || r.product.tier !== best.product.tier;
      });
      alternatives = differs.slice(0, 2);
      if (alternatives.length < 2) {
        rest.forEach(function (r) {
          if (alternatives.length < 2 && alternatives.indexOf(r) === -1) alternatives.push(r);
        });
      }
    }

    /* On a low-information path we return best: null and alternatives: [] on
       purpose. It makes it structurally impossible for the UI to render a
       "Best Match" or a Fit Match percentage from a one-answer profile, rather
       than relying on the renderer to remember not to. The full scored list is
       still returned as `all` for internal tests and diagnostics. */
    if (info.low) {
      return {
        best: null,
        alternatives: [],
        all: scored,
        neutral: scored.length > 0 && scored[0].neutral,
        lowInformation: true,
        lowInformationDetail: info,
        direction: directionFor(answers, products),
      };
    }

    return {
      best: best,
      alternatives: alternatives,
      all: scored,
      /* true only when every axis was answered "Not Sure". Not reachable from
         the customer UI now that Step 1 is a forced choice; retained for the
         internal scoring unit test. */
      neutral: scored.length > 0 && scored[0].neutral,
      lowInformation: false,
      lowInformationDetail: info,
      direction: null,
    };
  }

  global.PutterFinderScoring = {
    WEIGHTS: WEIGHTS,
    BEST_BONUS: BEST_BONUS,
    BALANCE_EXEMPT: BALANCE_EXEMPT,
    ANCHOR_AXES: ANCHOR_AXES,
    LOW_INFO_MIN_WEIGHT: LOW_INFO_MIN_WEIGHT,
    DIRECTIONS: DIRECTIONS,
    DIRECTION_COPY: DIRECTION_COPY,
    activeWeight: activeWeight,
    lowInformation: lowInformation,
    directionFor: directionFor,
    scoreProduct: scoreProduct,
    reasonsFor: reasonsFor,
    rank: rank,
    SPREAD_TIGHT: SPREAD_TIGHT,
    STRONG_MIN: STRONG_MIN,
    GOOD_MIN: GOOD_MIN,
    MATCH_LABELS: MATCH_LABELS,
    CLOSE_ALTERNATIVES_NOTE: CLOSE_ALTERNATIVES_NOTE,
    matchLabel: matchLabel,
    profileFor: profileFor,
    lengthsFor: lengthsFor,
  };
})(typeof window !== 'undefined' ? window : globalThis);
